// New issue files the issue at once, as today's app does, and opens it in the
// board's enlarged view (Space) with its title selected, to fill in there.
// Closed untouched, it goes to Cancelled: the board has no delete.
import { useEffect, useRef, useState } from 'react'
import type { Issue } from '../api/types'
import { PlusIcon } from '../components/Icons'
import { useStore } from '../store/context'
import { canInteract, useTabScope } from '../workspace/context'

/** A new issue's title until it is given one. */
export const NEW_ISSUE_TITLE = 'New issue'

/** True while a new issue is as it was filed: nothing typed, set or claimed. */
export function untouched(issue: Issue): boolean {
  return issue.title === NEW_ISSUE_TITLE && !issue.summary && !issue.description && !issue.plan.text && !issue.outcome
    && issue.labels.length === 0 && issue.priority === 'none' && issue.state === 'backlog' && !issue.column && !issue.claim
}

export function NewIssue({ projectId, compact = false, onCreated }: { projectId: string; compact?: boolean; onCreated: (issue: Issue) => void }) {
  const store = useStore()
  const scope = useTabScope()
  const [creating, setCreating] = useState(false)
  const busy = useRef(false)
  const open = async () => {
    if (busy.current) return
    busy.current = true
    setCreating(true)
    try {
      const issue = await store.createIssue(projectId, NEW_ISSUE_TITLE)
      if (issue) onCreated(issue)
    } finally {
      busy.current = false
      setCreating(false)
    }
  }
  useEffect(() => {
    const press = (event: KeyboardEvent) => {
      if (!canInteract(scope) || event.defaultPrevented) return
      if (event.key.toLowerCase() !== 'n' || event.metaKey || event.ctrlKey || event.altKey || event.repeat) return
      const target = event.target
      if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]')) return
      if ((scope?.surface() ?? document).querySelector('[role="dialog"]')) return
      event.preventDefault()
      void open()
    }
    document.addEventListener('keydown', press)
    return () => document.removeEventListener('keydown', press)
  })
  return <button type="button" className={compact ? 'button summary-new' : 'button button-small button-primary'} aria-label="New issue" aria-keyshortcuts="N" title="New issue N"
    disabled={creating} onClick={() => void open()}>
    {compact ? <PlusIcon size={14} /> : 'New issue'}
  </button>
}
